// La dictée découpée aux silences — reprise de Drakkar (/opt/drakkar/web/app.js,
// `dicteeFlux`), qui partage avec OGRYN le même service de transcription (3913).
//
// Envoyé d'un bloc, un récit de 35 s revenait en 49 signes, parfois en anglais
// (07/10) : Parakeet décroche au-delà de ~30 s. Découpé SUR LES SILENCES, chaque
// tranche reste courte, part pendant qu'on parle encore, et à l'arrêt il ne reste
// à attendre que la dernière. Couper toutes les N secondes trancherait des mots
// en deux moitiés que le modèle ne reconnaît ni l'une ni l'autre.

const PALIER = 0.014;     // RMS 0..1 au-dessus duquel on considère qu'on parle
const SILENCE_MS = 600;   // pause qui ferme une tranche
const MIN_MS = 1400;      // en dessous, un mot isolé : le modèle brode
const MAX_MS = 28000;     // la fenêtre du modèle, coupée même sans pause

export const doitCouper = (ecoule: number, depuisSon: number): boolean =>
  ecoule > MIN_MS && (depuisSon > SILENCE_MS || ecoule > MAX_MS);

/** Les tranches reviennent dans le désordre : on recolle par rang de prise.
 *  `null` = tranche que le service n'a pas transcrite. */
export const assembler = (recus: Map<number, string | null>): string =>
  [...recus.entries()].sort((a, b) => a[0] - b[0]).map(([, t]) => (t ?? '').trim())
    .filter(Boolean).join(' ').replace(/\s+/g, ' ');

/** Le texte, ou une erreur si RIEN n'est revenu et qu'une tranche a échoué : un
 *  service en panne n'est pas un silence (« Rien entendu » faisait tout redire). */
export const verdict = (recus: Map<number, string | null>): string => {
  const texte = assembler(recus);
  if (!texte && [...recus.values()].includes(null)) throw new Error('transcription injoignable');
  return texte;
};

const transcrire = async (blob: Blob): Promise<string> => {
  const r = await fetch('/api/otto/transcribe', {
    method: 'POST', headers: { 'Content-Type': blob.type || 'audio/webm' }, body: blob,
  });
  if (!r.ok) throw new Error(`transcription ${r.status}`);
  return ((await r.json())?.text ?? '').trim();
};

/** Enregistre `stream` par tranches transcrites au fil de l'eau. `onLevel` reçoit
 *  la même amplitude lissée que `meterStream` (le bouton bat au rythme de la voix).
 *  `arreter()` ferme la dernière tranche, attend les transcriptions et rend le texte
 *  — ou rejette si le service n'en a transcrit aucune. */
export function dicter(stream: MediaStream, onLevel?: (v: number) => void): { arreter: () => Promise<string> } {
  const ac = new AudioContext();
  const an = ac.createAnalyser();
  an.fftSize = 512;
  ac.createMediaStreamSource(stream).connect(an);
  const buf = new Uint8Array(an.fftSize);
  const recus = new Map<number, string | null>();
  const envois: Promise<void>[] = [];
  let rang = 0, debut = 0, dernierSon = 0, lisse = 0, raf = 0;
  let courant: { rec: MediaRecorder; morceaux: Blob[] } | null = null;

  // Chaque enregistreur a SON tableau : `dataavailable` arrive après `stop()`, un
  // tableau partagé versait la fin d'une tranche dans la suivante (Drakkar, 23/09).
  const ouvrir = () => {
    const c = { rec: new MediaRecorder(stream), morceaux: [] as Blob[] };
    c.rec.ondataavailable = e => { if (e.data.size) c.morceaux.push(e.data); };
    c.rec.start();
    courant = c;
    debut = dernierSon = performance.now();
  };
  const couper = (fin = false) => new Promise<void>(resolve => {
    const c = courant;
    if (!c) return resolve();
    const n = rang++;
    c.rec.onstop = () => {
      const blob = new Blob(c.morceaux, { type: c.rec.mimeType || 'audio/webm' });
      // Moins de 2 Ko : un souffle, que le modèle remplirait de mots inventés.
      if (blob.size > 2000) envois.push(transcrire(blob).then(t => { recus.set(n, t); }, () => { recus.set(n, null); }));
      resolve();
    };
    c.rec.stop();
    if (!fin) ouvrir();
  });
  const veiller = () => {
    an.getByteTimeDomainData(buf);
    let somme = 0;
    for (const v of buf) somme += ((v - 128) / 128) ** 2;
    const rms = Math.sqrt(somme / buf.length);
    lisse += (Math.min(1, rms * 4) - lisse) * 0.35;
    onLevel?.(lisse);
    const t = performance.now();
    if (rms > PALIER) dernierSon = t;
    if (doitCouper(t - debut, t - dernierSon)) void couper();
    raf = requestAnimationFrame(veiller);
  };
  ouvrir();
  veiller();

  return {
    arreter: async () => {
      cancelAnimationFrame(raf);
      await couper(true);
      stream.getTracks().forEach(p => p.stop());
      onLevel?.(0);
      void ac.close();
      await Promise.all(envois);
      return verdict(recus);
    },
  };
}

// L'onde de la dictée, comme celle de ChatGPT — reprise de Drakkar (projet.js,
// `ondeDictee`) : une barre par instant, la plus récente à droite, qui défile tant
// qu'on parle. Chaque barre garde le PIC de son instant — la moyenne aplatissait
// les syllabes. Au silence, il reste une rangée de points.
export const ONDE_PAS_MS = 70;
const LARGE = 3, ECART = 2;
export const ONDE_LARGE = LARGE;

export const barresOnde = (pics: number[], w: number, h: number) =>
  pics.slice(-Math.floor(w / (LARGE + ECART))).map((v, i, tous) => {
    const bh = Math.max(LARGE, v * h);
    return { x: w - (tous.length - i) * (LARGE + ECART), y: (h - bh) / 2, h: bh };
  });
